const FRAME_HEADER_BYTES = 4;
const LAYER_I_SLOT_BYTES = 4;
const BITRATE_INDEX_FREE_FORMAT = 0;
const BITRATE_INDEX_INVALID = 15;
const SAMPLE_RATE_INDEX_INVALID = 3;

// The sample-rate table is the one place all three MPEG versions differ, so it defines the version axis; MPEG 2.5 shares MPEG 2's rows everywhere else. Indexed by the header's sample-rate bits
const SAMPLE_RATES_HZ = {
  mpeg1: [44100, 48000, 32000],
  mpeg2: [22050, 24000, 16000],
  mpeg25: [11025, 12000, 8000],
};
type MpegVersion = keyof typeof SAMPLE_RATES_HZ;

// Indexed by the header's bitrate bits (layer II and III share a table for MPEG 2 and 2.5)
const MPEG1_BITRATES_KBPS = {
  1: [0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448],
  2: [0, 32, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 384],
  3: [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320],
};
const MPEG2_BITRATES_KBPS = {
  1: [0, 32, 48, 56, 64, 80, 96, 112, 128, 144, 160, 176, 192, 224, 256],
  2: [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160],
  3: [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160],
};
type Layer = keyof typeof MPEG1_BITRATES_KBPS;

const BITRATES_KBPS: Record<MpegVersion, Record<Layer, number[]>> = {
  mpeg1: MPEG1_BITRATES_KBPS,
  mpeg2: MPEG2_BITRATES_KBPS,
  mpeg25: MPEG2_BITRATES_KBPS,
};

const SAMPLES_PER_FRAME: Record<MpegVersion, Record<Layer, number>> = {
  mpeg1: { 1: 384, 2: 1152, 3: 1152 },
  mpeg2: { 1: 384, 2: 1152, 3: 576 },
  mpeg25: { 1: 384, 2: 1152, 3: 576 },
};

// Side info sits between the header (and CRC, when present) and the audio data; its size depends on MPEG version and channel mode
const SIDE_INFO_BYTES: Record<MpegVersion, { mono: number; stereo: number }> = {
  mpeg1: { mono: 17, stereo: 32 },
  mpeg2: { mono: 9, stereo: 17 },
  mpeg25: { mono: 9, stereo: 17 },
};

// Header bits to axis value; the reserved patterns (version bits 1, layer bits 0) have no entry, so a missing lookup is the validity check
const MPEG_VERSION_BY_BITS: Record<number, MpegVersion | undefined> = {
  3: "mpeg1",
  2: "mpeg2",
  0: "mpeg25",
};
const LAYER_BY_BITS: Record<number, Layer | undefined> = { 3: 1, 2: 2, 1: 3 };

// The one reader of an MPEG audio frame header (ISO 11172-3 / 13818-3): what the four bytes at `offset` declare, or null when they are not a well-formed header. Every MP3 walker and sniffer in the app judges headers through this, so "is this a frame" has one answer: the frame measurer counts frames with it and the backing-track lead-in reader confirms an MP3 stream with it.
export const parseMp3FrameHeader = (
  bytes: Uint8Array,
  offset: number
): {
  layer: Layer;
  sampleRate: number;
  samplesPerFrame: number;
  frameBytes: number;
  sideInfoBytes: number;
  hasCrc: boolean;
} | null => {
  if (offset + FRAME_HEADER_BYTES > bytes.length) {
    return null;
  }
  const [first, second, third, fourth] = bytes.subarray(
    offset,
    offset + FRAME_HEADER_BYTES
  );
  if (first !== 0xff || (second & 0xe0) !== 0xe0) {
    return null;
  }

  const version = MPEG_VERSION_BY_BITS[(second >> 3) & 0x3];
  const layer = LAYER_BY_BITS[(second >> 1) & 0x3];
  const bitrateIndex = (third >> 4) & 0xf;
  const sampleRateIndex = (third >> 2) & 0x3;
  const hasPadding = ((third >> 1) & 0x1) === 1;
  const hasCrc = (second & 0x1) === 0;
  const isMono = (fourth & 0xc0) === 0xc0;
  if (
    version === undefined ||
    layer === undefined ||
    bitrateIndex === BITRATE_INDEX_FREE_FORMAT ||
    bitrateIndex === BITRATE_INDEX_INVALID ||
    sampleRateIndex === SAMPLE_RATE_INDEX_INVALID
  ) {
    return null;
  }

  const sampleRate = SAMPLE_RATES_HZ[version][sampleRateIndex];
  const bitrate = 1000 * BITRATES_KBPS[version][layer][bitrateIndex];
  const samplesPerFrame = SAMPLES_PER_FRAME[version][layer];
  const paddingBytes = hasPadding ? 1 : 0;
  const frameBytes =
    layer === 1 ?
      (Math.floor((12 * bitrate) / sampleRate) + paddingBytes) *
      LAYER_I_SLOT_BYTES
    : Math.floor(((samplesPerFrame / 8) * bitrate) / sampleRate) + paddingBytes;

  return {
    layer,
    sampleRate,
    samplesPerFrame,
    frameBytes,
    sideInfoBytes: SIDE_INFO_BYTES[version][isMono ? "mono" : "stereo"],
    hasCrc,
  };
};
